import { createFileRoute, Link } from "@tanstack/react-router";
import { Suspense, lazy } from "react";
import { Layout } from "@/components/Layout";
import { ratesQueryOptions } from "@/components/CountryPage";
import { SITE_URL } from "@/lib/seo";
import { withTodayDate } from "@/lib/dateIntent";
import { buildToolSchemas } from "@/lib/tools-schema";

const JewelryEstimator = lazy(() =>
  import("@/components/GoldToolsSuite").then((m) => ({ default: m.JewelryEstimator })),
);

export const Route = createFileRoute("/tools/jewelry-making-estimator")({
  head: () => ({
    meta: [
      { title: withTodayDate("Jewelry Making Charges Calculator — Wastage & GST") },
      {
        name: "description",
        content:
          "Estimate the final price of any gold ornament. Add making charges (% or flat), wastage and 3% GST (India) on top of today's live 24K, 22K, 21K, 18K or 14K rate. Free jeweller bill calculator.",
      },
      { property: "og:title", content: "Jewelry Making Cost Estimator — Live Gold + Wastage + GST" },
      { property: "og:description", content: "Break down any jeweller's bill: gold value, wastage, making charges and GST." },
      { property: "og:url", content: `${SITE_URL}/tools/jewelry-making-estimator` },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/tools/jewelry-making-estimator` }],
    scripts: buildToolSchemas({
      name: "Jewelry Making Charges Calculator",
      description: "Add making charges, wastage and GST on top of the live gold rate to price any ornament.",
      path: "/tools/jewelry-making-estimator",
      steps: [
        { name: "Enter ornament weight and karat", text: "Type the ornament weight and pick the karat (22K, 21K, 18K)." },
        { name: "Add wastage and making", text: "Enter wastage percentage and making charge (per-gram or flat)." },
        { name: "Read final invoice", text: "See the final invoice with gold value, wastage, making and GST broken out." },
      ],
    }),
  }),
  loader: ({ context }) => {
    void context.queryClient.prefetchQuery(ratesQueryOptions);
  },
  component: Page,
});

function Page() {
  return (
    <Layout>
      <section className="mx-auto max-w-6xl px-4 pt-10 pb-2">
        <nav className="text-xs text-muted-foreground mb-3">
          <Link to="/tools" className="hover:text-gold">Tools</Link> / Jewelry Making Estimator
        </nav>
        <h1 className="text-3xl md:text-4xl font-black tracking-tight">Jewelry Making Cost Estimator</h1>
        <p className="mt-3 text-muted-foreground max-w-3xl">
          Catch jeweller overcharges before you pay. This calculator adds making charges, wastage % and
          GST (3% India) to today's live gold base rate so you see exactly where every rupee goes.
        </p>
      </section>
      <Suspense fallback={<div className="py-12 text-center text-sm text-muted-foreground">Loading…</div>}>
        <JewelryEstimator />
      </Suspense>
    </Layout>
  );
}